import { lazy, Suspense, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ClientOnly } from "@tanstack/react-router";
import {
  Accessibility,
  ArrowRight,
  CalendarDays,
  ChevronRight,
  Clock3,
  Info,
  Navigation,
  Star,
  TrainFront,
  UtensilsCrossed,
} from "lucide-react";
import { ParkingMark, TramMark } from "@/components/icons/VisitMarks";
import { MapMarkerPinIcon } from "@/components/icons/MapMarkerPinIcon";
import { CalendarIcon, CompassIcon, PlateIcon } from "@/components/icons/NavIcons";

import { DISTRICT_SECTION_KEYS, getPublicDistrict } from "@/lib/districts.functions";
import { useHideGlobalMapFab } from "@/lib/map-fab-visibility";
import { DistrictVenueList } from "@/components/districts/DistrictVenueList";
import { DistrictVenueRail } from "@/components/districts/DistrictVenueRail";
import { DistrictLunchRail, hasLunch } from "@/components/districts/DistrictLunchRail";
import MarqueeRail, { duplicateForMarquee } from "@/components/MarqueeRail";
import { SmartImage } from "@/components/ui/SmartImage";
import { computeOpenStatus } from "@/lib/opening-hours";
import { type AreaPoly,
  type AreaStyle, IsometricDistrictMap, type IsoPin, type ManualPin } from "@/components/districts/IsometricDistrictMap";
import type { AreaPin } from "@/components/districts/DistrictAreaMap";



const AreaMap = lazy(() => import("@/components/districts/DistrictAreaMap"));



export const districtQuery = (slug: string) =>
  queryOptions({
    queryKey: ["public-district", slug],
    queryFn: () => getPublicDistrict({ data: { slug } }),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "restaurants", label: "Restaurants" },
  { key: "do", label: "Things to do" },
  { key: "events", label: "Events" },
  { key: "map", label: "Map" },
  { key: "plan", label: "Plan your visit" },
  { key: "gallery", label: "Gallery" },
  { key: "about", label: "About" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const UI = '"Inter Variable", Inter, ui-sans-serif, system-ui, sans-serif';

export function DistrictHubPage({ slug }: { slug: string }) {
  useHideGlobalMapFab();
  const { data } = useSuspenseQuery(districtQuery(slug));
  const [tab, setTab] = useState<TabKey>("overview");
  const goTab = (next: TabKey) => {
    setTab(next);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const allVenues = data?.venues ?? [];
  const venues = useMemo(() => allVenues.filter((v) => v.section !== "do"), [allVenues]);
  const doVenues = useMemo(() => allVenues.filter((v) => v.section === "do"), [allVenues]);
  const events = data?.events ?? [];

  const pins = useMemo(
    () =>
      allVenues
        .filter((v) => typeof v.lat === "number" && typeof v.lng === "number")
        .map((v) => ({
          id: v.id,
          label: v.name,
          address: v.address,
          lat: v.lat as number,
          lng: v.lng as number,
          slug: v.slug,
          category: v.category,
        })),
    [allVenues],
  );

  const areaPins: AreaPin[] = useMemo(() => {
    const isBar = (v: (typeof allVenues)[number]) =>
      /bar|pub|cocktail|brewery|drink/i.test(`${v.category ?? ""} ${v.tags?.join(" ") ?? ""}`);
    const venuePins: AreaPin[] = allVenues
      .filter((v) => typeof v.lat === "number" && typeof v.lng === "number")
      .map((v) => ({
        id: v.id,
        kind: v.section === "do" ? "do" : isBar(v) ? "drink" : "eat",
        label: v.name,
        sublabel: v.address ?? v.area,
        lat: v.lat as number,
        lng: v.lng as number,
        slug: v.slug,
      }));
    const eventPins: AreaPin[] = events
      .filter((e) => typeof e.lat === "number" && typeof e.lng === "number")
      .map((e) => ({
        id: `ev-${e.id}`,
        kind: "event" as const,
        label: e.name,
        sublabel: e.date_label ?? e.address,
        lat: e.lat as number,
        lng: e.lng as number,
        slug: e.slug,
      }));
    const infraPins: AreaPin[] = (data?.district.map_points ?? []).map((p, i) => ({
      id: `mp-${i}`,
      kind: p.kind,
      label: p.label,
      sublabel: p.note ?? null,
      lat: p.lat,
      lng: p.lng,
    }));
    return [...venuePins, ...eventPins, ...infraPins];
  }, [allVenues, events, data]);


  const page = data?.district.page;
  const showLunch = page?.sections.lunch !== false;
  const showEvents = page?.sections.events !== false;
  const showMap = page?.sections.map !== false;
  const showInfo = page?.sections.info !== false;
  const showRestaurants = page?.sections.restaurants !== false;
  const showDo = page?.sections.do !== false;
  const showMenus = page?.sections.menus !== false;
  const menuPlaces = data?.menus ?? [];

  const lunchVenues = useMemo(() => {
    if (page?.lunch_ids.length) return data?.lunch ?? [];
    if (data?.lunch?.length) return data.lunch;
    return venues.filter((v) => hasLunch(v)).slice(0, 12);
  }, [data?.lunch, page?.lunch_ids, venues]);
  const eatVenues = useMemo(() => {
    if (page?.restaurant_ids.length) {
      const byId = new Map(venues.map((v) => [v.id, v]));
      return page.restaurant_ids.map((id) => byId.get(id)).filter((v): v is (typeof venues)[number] => !!v);
    }
    return venues;
  }, [page?.restaurant_ids, venues]);
  const activityVenues = useMemo(() => {
    if (page?.do_ids.length) {
      const byId = new Map(doVenues.map((v) => [v.id, v]));
      return page.do_ids.map((id) => byId.get(id)).filter((v): v is (typeof doVenues)[number] => !!v);
    }
    return doVenues;
  }, [page?.do_ids, doVenues]);

  const lunchOn = showLunch && lunchVenues.length > 0;
  const eventsOn = showEvents && events.length > 0;
  const eatOn = showRestaurants && eatVenues.length > 0;
  const doOn = showDo && activityVenues.length > 0;
  let marqueeIndex = 0;
  const nextMarqueeDir = (): "left" | "right" => (marqueeIndex++ % 2 === 0 ? "right" : "left");
  const lunchDir = lunchOn ? nextMarqueeDir() : "right";
  const eventsDir = eventsOn ? nextMarqueeDir() : "left";
  const eatDir = eatOn ? nextMarqueeDir() : "right";
  const doDir = doOn ? nextMarqueeDir() : "left";

  const gallery = useMemo(
    () => allVenues.filter((v) => !!v.image).map((v) => ({ id: v.id, slug: v.slug, name: v.name, image: v.image as string })),
    [venues],
  );

  if (!data) return null;
  const { district } = data;
  const openNow = venues.filter((v) => ["open", "closing_soon"].includes(computeOpenStatus(v).state)).length;
  const isTrainFactory = district.slug === "train-factory" || district.slug === "konepaja";
  const venueHero = venues.find((v) => v.image)?.image ?? null;
  const heroImage = district.hero_image_url ?? venueHero;
  const heroTagline = district.tagline ?? (isTrainFactory ? "The urban heart of Helsinki." : null);

  const tabsToShow = TABS.filter((t) => {
    if (t.key === "events") return showEvents && events.length > 0;
    if (t.key === "do") return showDo && activityVenues.length > 0;
    if (t.key === "restaurants") return showRestaurants && eatVenues.length > 0;
    
    if (t.key === "gallery") return gallery.length > 0;
    if (t.key === "about") return !!district.description;
    return true;
  });

  return (
    <div className="min-h-screen bg-white pb-20 sm:pb-14" style={{ fontFamily: UI, fontStyle: "normal" }}>
      <header
        className={
          isTrainFactory
            ? "relative h-[236px] overflow-hidden sm:h-[300px] lg:h-[380px]"
            : "relative h-[210px] overflow-hidden sm:h-[360px] lg:h-[440px]"
        }
      >
        <div className="absolute inset-0">
          {heroImage ? (
            <SmartImage
              src={heroImage}
              alt=""
              loading="eager"
              fetchPriority="high"
              className={
                isTrainFactory
                  ? "size-full object-cover object-center sm:object-[center_42%]"
                  : "size-full object-cover object-[center_40%]"
              }
            />
          ) : (
            <div className="size-full bg-gradient-to-br from-navy via-royal to-navy" />
          )}
          <div
            aria-hidden
            className="absolute inset-0 sm:hidden"
            style={{
              background: isTrainFactory
                ? "linear-gradient(180deg, rgba(8,16,32,0.08) 0%, rgba(8,16,32,0.0) 28%, rgba(8,16,32,0.72) 100%)"
                : "linear-gradient(90deg, rgba(11,20,40,0.72) 0%, rgba(11,20,40,0.28) 46%, rgba(11,20,40,0.06) 100%)",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 hidden sm:block"
            style={{
              background: isTrainFactory
                ? "linear-gradient(100deg, rgba(8,16,32,0.72) 0%, rgba(8,16,32,0.32) 38%, rgba(8,16,32,0.08) 68%, rgba(8,16,32,0.0) 100%)"
                : "linear-gradient(105deg, rgba(11,29,58,0.82) 0%, rgba(11,29,58,0.48) 42%, rgba(11,29,58,0.14) 100%)",
            }}
          />
        </div>

        <div
          className={
            isTrainFactory
            ? "relative mx-auto flex h-full max-w-6xl flex-col justify-end px-4 pb-3.5 pt-3 sm:px-6 sm:pb-8 sm:pt-6"
              : "relative mx-auto flex h-full max-w-6xl flex-col justify-start px-4 pb-4 pt-3.5 sm:justify-end sm:px-6 sm:pb-10 sm:pt-6"
          }
        >
          <nav aria-label="Breadcrumb" className="mb-auto hidden sm:block">
            <ol className="flex flex-wrap items-center gap-2 text-[11px] font-medium text-white/70">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li aria-hidden className="text-white/35">/</li>
              <li><Link to="/districts" className="hover:text-white">Districts</Link></li>
              <li aria-hidden className="text-white/35">/</li>
              <li className="text-white">{district.name}</li>
            </ol>
          </nav>

          <div className="max-w-xl">
            {district.official_partner ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#0b1d3a]/70 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-white backdrop-blur-sm sm:gap-1.5 sm:px-3 sm:text-[10px] sm:tracking-[0.16em]">
                <Star className="size-2.5 fill-current sm:size-3" /> Official district partner
              </span>
            ) : null}
            <h1 className="mt-1.5 font-heading text-[30px] leading-[1.02] tracking-tight text-white drop-shadow-[0_2px_10px_rgba(11,20,40,0.45)] sm:mt-3 sm:text-5xl sm:uppercase sm:leading-[0.88] sm:drop-shadow-none lg:text-[56px] lg:leading-[0.92]">
              {isTrainFactory ? (
                <>
                  Train Factory
                  <span className="mt-0.5 block text-[15px] font-medium tracking-normal text-white/80 sm:mt-0 sm:text-[inherit] sm:font-[inherit] sm:tracking-tight sm:text-white">
                    District
                  </span>
                </>
              ) : (
                district.name
              )}
            </h1>
            {heroTagline ? (
              <p className="mt-1.5 max-w-[36ch] text-[13px] font-medium leading-snug text-white/90 drop-shadow-[0_1px_8px_rgba(11,20,40,0.4)] sm:mt-3 sm:max-w-md sm:text-[16px] sm:drop-shadow-none">
                {isTrainFactory ? (
                  <>
                    <span className="sm:hidden">{heroTagline}</span>
                    <span className="hidden sm:inline">{district.tagline ?? heroTagline}</span>
                  </>
                ) : (
                  heroTagline
                )}
              </p>
            ) : null}
            {venues.length > 0 || events.length > 0 || openNow > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 sm:mt-5 sm:gap-2">
              {venues.length > 0 ? (
                <HeroStat icon={<UtensilsCrossed className="size-3.5" />} value={String(venues.length)} label="Places" />
              ) : null}
              {events.length > 0 ? (
                <HeroStat icon={<CalendarDays className="size-3.5" />} value={String(events.length)} label="Events" />
              ) : null}
              {openNow > 0 ? (
                <HeroStat
                  icon={<Clock3 className="size-3.5" />}
                  value={`${openNow}`}
                  label="Open now"
                  dot
                />
              ) : null}
            </div>
            ) : null}
          </div>
        </div>
      </header>

      <div className="sticky top-3 z-30 hidden sm:block">
        <div className="mx-auto flex h-16 max-w-6xl items-stretch justify-between rounded-2xl border border-navy/[0.08] bg-white/80 px-5 shadow-[0_12px_32px_-16px_rgba(15,33,71,0.18)] backdrop-blur-xl lg:px-6">
          <nav
            aria-label="District sections"
            className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {tabsToShow.map((t) => {
              const active = tab === t.key;
              const count = t.key === "restaurants" ? venues.length : t.key === "do" ? doVenues.length : t.key === "events" ? events.length : 0;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`group relative flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-[13px] leading-none transition-all duration-200 lg:px-3.5 ${
                    active
                      ? "font-semibold text-navy"
                      : "font-medium text-navy/45 hover:text-navy"
                  }`}
                >
                  <span
                    className={`relative z-10 rounded-lg px-1 py-1 transition-colors duration-200 ${
                      active ? "" : "group-hover:bg-navy/[0.04]"
                    }`}
                  >
                    {t.label}
                  </span>
                  {count > 0 ? (
                    <span
                      className={`relative z-10 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full border px-1 text-[10px] font-bold tabular-nums transition-colors duration-200 ${
                        active
                          ? "border-navy/10 bg-navy text-white"
                          : "border-navy/[0.08] bg-navy/[0.05] text-navy/55 group-hover:bg-navy/10 group-hover:text-navy/75"
                      }`}
                    >
                      {count}
                    </span>
                  ) : null}
                  {active ? (
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-xl bg-navy/[0.07] transition-transform duration-200"
                    />
                  ) : null}
                </button>
              );
            })}
          </nav>
          {district.address ? (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(district.address)}`}
              target="_blank"
              rel="noreferrer"
              className="ml-4 inline-flex shrink-0 items-center gap-2 self-center rounded-full bg-navy px-4 py-2 text-[12.5px] font-semibold leading-none text-white shadow-[0_6px_16px_-6px_rgba(15,33,71,0.45)] transition-all duration-200 hover:-translate-y-px hover:shadow-[0_10px_20px_-6px_rgba(15,33,71,0.5)] active:scale-95"
            >
              <Navigation className="size-3.5" strokeWidth={2.25} />
              <span>Directions</span>
            </a>
          ) : (
            <button
              type="button"
              onClick={() => goTab("map")}
              className="ml-4 inline-flex shrink-0 items-center gap-2 self-center rounded-full bg-navy px-4 py-2 text-[12.5px] font-semibold leading-none text-white shadow-[0_6px_16px_-6px_rgba(15,33,71,0.45)] transition-all duration-200 hover:-translate-y-px hover:shadow-[0_10px_20px_-6px_rgba(15,33,71,0.5)] active:scale-95"
            >
              <MapMarkerPinIcon className="size-3.5" />
              <span>Open map</span>
            </button>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 pt-4 sm:px-6 sm:pt-10 lg:pt-12">
        {/* ---------------------------------------------------------- OVERVIEW */}
        {tab === "overview" ? (
          <div className="space-y-5 sm:space-y-12 lg:space-y-14">
            <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4 lg:gap-5">
              <QuickTile
                icon={<PlateIcon size={16} className="sm:size-8 lg:size-10" />}
                title="Eat & drink"
                sub="Restaurants & cafés"
                onClick={() => goTab("restaurants")}
              />
              <QuickTile
                icon={<CompassIcon size={16} className="sm:size-8 lg:size-10" />}
                title="Things to do"
                sub="Experiences & activities"
                onClick={() => goTab(activityVenues.length > 0 ? "do" : "restaurants")}
              />
              <QuickTile
                icon={<CalendarIcon size={16} className="sm:size-8 lg:size-10" />}
                title="Events"
                sub="What’s on this week"
                onClick={() => goTab("events")}
              />
              <QuickTile
                icon={<MapMarkerPinIcon className="size-4 sm:size-8 lg:size-10" />}
                title="Map"
                sub="Explore the district"
                onClick={() => goTab("map")}
              />
            </div>


            {(page?.order?.length ? page.order : DISTRICT_SECTION_KEYS).map((sectionKey) => {
              if (sectionKey === "lunch") {
                return showLunch && lunchVenues.length > 0 ? (
                  <section key="lunch">
                    <div className="mb-3 flex items-end justify-between gap-3 sm:mb-5">
                      <div className="min-w-0">
                        <p className="hidden text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40 sm:block">Eat</p>
                        <h2 className="mt-0.5 flex min-w-0 items-center gap-2.5 text-[18px] font-semibold leading-none tracking-tight text-navy sm:font-heading sm:text-[26px]">
                          <UtensilsCrossed className="hidden size-5 shrink-0 text-navy sm:block" />
                          <span className="truncate">Lunch spots</span>
                        </h2>
                      </div>
                      <TabLink
                        onClick={() => setTab("restaurants")}
                        className="shrink-0 text-[12px] font-medium text-navy/50 hover:text-navy sm:inline-flex sm:h-9 sm:items-center sm:rounded-full sm:border sm:border-navy/12 sm:bg-white sm:px-3.5 sm:text-[12px] sm:font-semibold sm:text-navy/70 sm:hover:border-navy/25"
                      >
                        <span className="sm:hidden">View all lunch places →</span>
                        <span className="hidden sm:inline">View all</span>
                        <ChevronRight className="ml-0.5 hidden size-3.5 sm:inline" />
                      </TabLink>
                    </div>
                    <DistrictLunchRail venues={lunchVenues} direction={lunchDir} />
                  </section>
                ) : null;
              }

              if (sectionKey === "menus") {
                return showMenus && menuPlaces.length > 0 ? (
                  <section key="menus">
                    <SectionHeading eyebrow="On the menu" title="Dishes in the district" />
                    <div className="grid gap-4 sm:grid-cols-2">
                      {menuPlaces.map((mp) => (
                        <div
                          key={mp.place_id}
                          className="rounded-2xl border border-navy/[0.10] bg-white p-4 shadow-sm sm:p-5"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="min-w-0 truncate text-[15px] font-semibold text-navy">{mp.name}</h3>
                            <Link
                              to="/menu/$slug"
                              params={{ slug: mp.slug }}
                              className="shrink-0 text-[12px] font-semibold text-navy/60 hover:text-navy"
                            >
                              Full menu
                              <ChevronRight className="ml-0.5 inline size-3.5" />
                            </Link>
                          </div>
                          <ul className="mt-3 divide-y divide-navy/10">
                            {mp.dishes.slice(0, 4).map((dish) => (
                              <li key={dish.id} className="flex items-start justify-between gap-3 py-2.5">
                                <div className="min-w-0">
                                  <p className="truncate text-[13px] font-medium text-navy">{dish.name}</p>
                                  {dish.description ? (
                                    <p className="mt-0.5 line-clamp-1 text-[12px] text-navy/60">{dish.description}</p>
                                  ) : null}
                                </div>
                                {dish.price !== null ? (
                                  <span className="shrink-0 text-[13px] font-semibold tabular-nums text-navy">
                                    {dish.price.toFixed(2)} {dish.currency === "EUR" ? "€" : dish.currency}
                                  </span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                          {mp.dishes.length > 4 ? (
                            <p className="mt-2 text-[12px] text-navy/50">+{mp.dishes.length - 4} more dishes</p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null;
              }

              if (sectionKey === "events") {

                return showEvents ? (
                  <section key="events" className="overflow-hidden rounded-2xl bg-navy p-5 sm:p-8 lg:p-10">
                    <div className="mx-auto max-w-2xl text-center">
                      <h2 className="font-heading text-[22px] leading-[1.1] tracking-tight text-white sm:text-[28px] lg:text-[32px]">
                        What’s on this week?
                      </h2>
                      <p className="mx-auto mt-2 max-w-md text-[13px] font-medium leading-snug text-white/70 sm:mt-3 sm:text-[15px]">
                        Don’t miss what’s happening in the district.
                      </p>
                      <button
                        type="button"
                        onClick={() => goTab("events")}
                        className="mt-4 inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-4 text-[13px] font-semibold leading-none text-navy normal-case shadow-sm transition hover:bg-white/90 sm:mt-5 sm:h-11 sm:gap-2 sm:px-5 sm:text-[14px]"
                      >
                        See all events
                        <ChevronRight className="size-4 sm:size-[18px]" strokeWidth={2.4} />
                      </button>
                    </div>
                    {events.length > 0 ? (
                      <div className="mt-6 min-w-0 sm:mt-8">
                        <MarqueeRail
                          className="!mx-0 px-0 [mask-image:none] [-webkit-mask-image:none]"
                          trackClassName="marquee-track gap-3 sm:gap-4"
                          direction={eventsDir}
                        >
                          {duplicateForMarquee(events).map((ev, i) => (
                            <EventTile key={`${ev.id}-${i}`} ev={ev} />
                          ))}
                        </MarqueeRail>
                      </div>
                    ) : null}
                  </section>

                ) : null;
              }

              if (sectionKey === "map") {
                return showMap ? (
                  <OpenMapCard
                    key="map"
                    districtName={district.name}
                    address={district.address}
                    onOpenMap={() => goTab("map")}
                    onPlan={() => setTab("plan")}
                  />
                ) : null;
              }

              if (sectionKey === "restaurants") {
                return showRestaurants && eatVenues.length > 0 ? (
                  <section key="restaurants">
                    <SectionHeading
                      eyebrow="Eat & drink"
                      title={`${eatVenues.length} Restaurants & bars`}
                      action={
                        <TabLink
                          onClick={() => setTab("restaurants")}
                          className="text-[12px] font-medium text-navy/50 hover:text-navy sm:inline-flex sm:h-9 sm:items-center sm:rounded-full sm:border sm:border-navy/12 sm:bg-white sm:px-3.5 sm:font-semibold sm:text-navy/70 sm:hover:border-navy/25"
                        >
                          View all
                          <ChevronRight className="ml-0.5 hidden size-3.5 sm:inline" />
                        </TabLink>
                      }
                    />
                    <DistrictVenueRail venues={eatVenues} direction={eatDir} />
                  </section>
                ) : null;
              }

              if (sectionKey === "do") {
                return showDo && activityVenues.length > 0 ? (
                  <section key="do">
                    <SectionHeading
                      eyebrow="Things to do"
                      title="Sports, exhibitions & coworking"
                      action={
                        <TabLink
                          onClick={() => setTab("do")}
                          className="text-[12px] font-medium text-navy/50 hover:text-navy sm:inline-flex sm:h-9 sm:items-center sm:rounded-full sm:border sm:border-navy/12 sm:bg-white sm:px-3.5 sm:font-semibold sm:text-navy/70 sm:hover:border-navy/25"
                        >
                          View all
                          <ChevronRight className="ml-0.5 hidden size-3.5 sm:inline" />
                        </TabLink>
                      }
                    />
                    <DistrictVenueRail venues={activityVenues} direction={doDir} />
                  </section>
                ) : null;
              }

              if (sectionKey === "info") {
                return showInfo ? (
                  <section key="info" className="hidden sm:block">
                    <SectionHeading eyebrow="Good to know" title="Getting here & practical info" />
                    <InfoCards district={district} onTab={setTab} />
                  </section>
                ) : null;
              }

              return null;
            })}

            {areaPins.length > 0 ? (
              <section className="hidden md:block">
                <SectionHeading eyebrow="Find your way" title={`Explore ${district.name} on the map`} />
                <MapPanel
                  districtSlug={district.slug}
                  pins={pins}
                  areaPins={areaPins}
                  center={district.lat && district.lng ? { lat: district.lat, lng: district.lng } : null}
                  districtName={district.name}
                />
              </section>
            ) : null}

          </div>
        ) : null}


        {/* ------------------------------------------------------- RESTAURANTS */}
        {tab === "restaurants" ? (
          <section>
            <SectionHeading eyebrow="Eat & drink" title={`${eatVenues.length} Restaurants & bars`} />
            {eatVenues.length > 0 ? (
              <DistrictVenueList venues={eatVenues} />
            ) : (
              <EmptyState text="No venues have been linked to this district yet." />
            )}
          </section>
        ) : null}

        {/* --------------------------------------------------------- THINGS TO DO */}
        {tab === "do" ? (
          <section>
            <SectionHeading
              eyebrow="Things to do"
              title={`${activityVenues.length} Things to do`}
            />
            {activityVenues.length > 0 ? (
              <DistrictVenueList venues={activityVenues} />
            ) : (
              <EmptyState text="No activities have been added to this district yet." />
            )}
          </section>
        ) : null}

        {/* ------------------------------------------------------------ EVENTS */}
        {tab === "events" ? (
          <section>
            <SectionHeading eyebrow="What's on" title="Upcoming events" />
            <EventGrid events={events} />
          </section>
        ) : null}

        {/* --------------------------------------------------------------- MAP */}
        {tab === "map" ? (
          <section>
            <SectionHeading eyebrow="Find your way" title="Area map" />
            <MapPanel
              districtSlug={district.slug}
              pins={pins}
              areaPins={areaPins}
              center={district.lat && district.lng ? { lat: district.lat, lng: district.lng } : null}
              districtName={district.name}
              tall
            />
          </section>
        ) : null}

        {/* -------------------------------------------------------------- PLAN */}
        {tab === "plan" ? (
          <section>
            <SectionHeading eyebrow="Plan your visit" title="Practical information" />
            <InfoCards district={district} onTab={setTab} />
          </section>
        ) : null}

        {/* ----------------------------------------------------------- GALLERY */}
        {tab === "gallery" ? (
          <section>
            <SectionHeading eyebrow="Photos" title="Gallery" />
            <div className="columns-2 gap-4 sm:columns-3 [&>*]:mb-4">
              {gallery.map((g) => (
                <Link
                  key={g.id}
                  to="/place/$id"
                  params={{ id: g.slug }}
                  className="group block overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm"
                >
                  <SmartImage
                    src={g.image}
                    alt={g.name}
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {/* ------------------------------------------------------------- ABOUT */}
        {tab === "about" && district.description ? (
          <section className="max-w-3xl">
            <SectionHeading eyebrow="About" title={`About ${district.name}`} />
            <p style={{ textTransform: "none" }} className="whitespace-pre-line text-base leading-relaxed text-navy/75">
              {district.description}
            </p>
            {district.website ? (
              <a
                href={district.website}
                target="_blank"
                rel="noreferrer"
                style={{ textTransform: "none" }}
                className="mt-6 inline-block text-sm font-semibold text-royal hover:underline"
              >
                Visit official website →
              </a>
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  );
}

function QuickTile({
  icon,
  title,
  sub,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full min-w-0 items-center gap-2 rounded-[0.875rem] border border-navy/[0.10] bg-white px-2 py-2 text-left transition-all duration-200 hover:-translate-y-1 hover:border-navy/25 hover:shadow-[0_24px_44px_-24px_rgba(15,27,61,0.45)] sm:gap-5 sm:rounded-[1.25rem] sm:px-6 sm:py-6 lg:gap-3 lg:rounded-[1.5rem] lg:px-4 lg:py-6"
    >
      <span className="grid size-7 shrink-0 place-items-center text-navy transition-colors sm:size-14 lg:size-12 lg:rounded-xl lg:bg-navy lg:text-white lg:shadow-[0_12px_24px_-12px_rgba(15,27,61,0.55)] lg:group-hover:bg-navy/90">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] font-bold leading-[1.15] tracking-tight text-navy sm:text-[22px] lg:text-[18px]">
          {title}
        </span>
        <span className="mt-px block truncate text-[9.5px] font-medium leading-[1.25] text-navy/45 sm:mt-1 sm:text-[16px] lg:text-[12px] lg:leading-snug">
          {sub}
        </span>
      </span>
      <ChevronRight className="hidden size-4 shrink-0 text-navy/30 transition-all duration-200 group-hover:translate-x-1 group-hover:text-navy lg:block" strokeWidth={2.2} />
    </button>
  );
}


function eventKind(ev: { category?: string | null; tags?: string[] }) {
  const c = (ev.category ?? "").trim();
  if (c) {
    const short = c.split(/[&,/]/)[0].trim();
    return (short || c).slice(0, 16);
  }
  return ((ev.tags ?? [])[0] ?? "Event").slice(0, 16);
}

function eventWhen(ev: { date_label?: string | null; starts_at?: string | null; ends_at?: string | null }) {
  if (ev.starts_at) {
    const s = new Date(ev.starts_at);
    if (!Number.isNaN(s.getTime())) {
      const day = s.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      const t1 = s.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
      if (!ev.ends_at) return `${day} · ${t1}`;
      const e = new Date(ev.ends_at);
      if (Number.isNaN(e.getTime())) return `${day} · ${t1}`;
      const t2 = e.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
      return `${day} · ${t1} – ${t2}`;
    }
  }
  return ev.date_label ?? "";
}

function EventTile({
  ev,
}: {
  ev: {
    id: string;
    slug: string;
    name: string;
    image: string | null;
    category?: string | null;
    tags?: string[];
    address?: string | null;
    area?: string | null;
    date_label?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
    price?: string | null;
  };
}) {
  const d = ev.starts_at ? new Date(ev.starts_at) : null;
  const when = eventWhen(ev);
  const kind = eventKind(ev);
  const location = ev.address ?? ev.area ?? "";
  return (
    <Link
      to="/place/$id"
      params={{ id: ev.slug }}
      className="group relative w-[160px] shrink-0 overflow-hidden rounded-xl bg-navy/50 sm:w-[212px] lg:w-[248px]"
    >
      <div className="relative aspect-[3/4]">
        {ev.image ? (
          <SmartImage
            src={ev.image}
            alt={ev.name}
            className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="size-full bg-navy/40" />
        )}
        <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
        {d && !Number.isNaN(d.getTime()) ? (
          <span className="absolute left-2.5 top-2.5 flex h-[46px] w-[42px] flex-col items-center justify-center rounded-[10px] bg-white shadow-sm sm:left-3 sm:top-3 sm:h-[52px] sm:w-[48px]">
            <span className="text-[7px] font-bold uppercase leading-none tracking-[0.16em] text-navy/50 sm:text-[8px]">
              {d.toLocaleDateString("en-US", { month: "short" }).slice(0, 3)}
            </span>
            <span className="mt-0.5 font-heading text-[17px] leading-none text-navy sm:text-[20px]">{d.getDate()}</span>
          </span>
        ) : null}
        <span className="absolute inset-x-2.5 bottom-2.5 sm:inset-x-3 sm:bottom-3">
          <span className="inline-block max-w-full truncate rounded-md bg-royal px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.12em] leading-none text-white sm:px-2 sm:text-[9px]">
            {kind}
          </span>
          <span className="mt-1.5 block line-clamp-2 text-[13px] font-semibold leading-[1.2] text-white sm:mt-2 sm:text-[15px]">
            {ev.name}
          </span>
          {location ? (
            <span className="mt-0.5 block line-clamp-1 text-[10px] font-medium leading-snug text-white/80 sm:text-[11px]">
              {location}
            </span>
          ) : null}
          {when ? (
            <span className="mt-0.5 block line-clamp-1 text-[10px] font-medium leading-snug text-white/70 sm:text-[11px]">
              {when}
            </span>
          ) : null}
        </span>
      </div>
    </Link>
  );
}

function OpenMapCard({
  districtName,
  address,
  onOpenMap,
  onPlan,
}: {
  districtName: string;
  address?: string | null;
  onOpenMap: () => void;
  onPlan: () => void;
}) {
  return (
    <section className="overflow-hidden rounded-[1.5rem] border border-navy/[0.08] bg-white shadow-[0_1px_2px_rgba(15,27,61,0.04)] lg:hidden">
      <div className="flex items-center gap-3 bg-navy px-4 py-4 sm:gap-4 sm:px-5 sm:py-[18px]">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-white/10 text-white sm:size-12">
          <MapMarkerPinIcon className="size-5 sm:size-6" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-[16px] leading-none tracking-tight text-white sm:text-[20px]">
            Explore {districtName}
          </h3>
          <p className="mt-1 text-[11px] font-medium leading-snug text-white/70 sm:text-[13px]">
            Find restaurants, events and services nearby.
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenMap}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-3 py-2 text-[12px] font-semibold leading-none text-navy transition hover:bg-white/90 sm:gap-1.5 sm:px-4 sm:text-[13px]"
        >
          Open map
          <ChevronRight className="size-3.5 sm:size-4" strokeWidth={2.4} />
        </button>
      </div>
      <div className="grid grid-cols-3 divide-x divide-navy/[0.08]">
        <PracticalLink
          icon={<Navigation className="size-[18px]" strokeWidth={2.25} />}
          label="Directions"
          href={address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}` : null}
          onClick={onPlan}
        />
        <PracticalLink
          icon={<ParkingMark className="size-[18px]" />}
          label="Parking"
          href={address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`parking ${address}`)}` : null}
          onClick={onPlan}
        />
        <PracticalLink
          icon={
            <>
              <TrainFront className="size-[18px] sm:hidden" strokeWidth={2} />
              <TramMark className="hidden size-[18px] sm:block" />
            </>
          }
          label="Public transport"
          href="https://www.hsl.fi/en"
          onClick={onPlan}
        />
      </div>
    </section>
  );
}

function PracticalLink({
  icon,
  label,
  href,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  href?: string | null;
  onClick?: () => void;
}) {
  const cls =
    "group flex min-h-[76px] sm:min-h-[92px] flex-col items-center justify-center gap-2 px-1 py-3 sm:gap-2.5 sm:py-4 text-[11px] sm:text-[13px] font-semibold leading-none text-navy normal-case transition-colors hover:bg-navy/[0.03]";
  const inner = (
    <>
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-navy/[0.06] text-navy transition-colors group-hover:bg-navy group-hover:text-white sm:size-11">
        {icon}
      </span>
      <span className="text-center leading-tight">{label}</span>
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

function HeroStat({

  icon,
  value,
  label,
  dot,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
  dot?: boolean;
}) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/15 backdrop-blur-md">
      <span className="text-white/80">{icon}</span>
      <span className="flex items-baseline gap-1.5">
        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold leading-none text-white">
          {dot ? <span className="size-1.5 animate-pulse rounded-full bg-emerald-400" /> : null}
          {value}
        </span>
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/60">{label}</span>
      </span>
    </div>
  );
}

function EventGrid({ events }: { events: { id: string; slug: string; name: string; image: string | null; date_label?: string | null; starts_at?: string | null; price?: string | null }[] }) {
  if (events.length === 0) return <EmptyState text="No upcoming events in this district." />;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {events.map((ev) => {
        const d = ev.starts_at ? new Date(ev.starts_at) : null;
        return (
          <Link
            key={ev.id}
            to="/place/$id"
            params={{ id: ev.slug }}
            className="group overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm transition-shadow hover:shadow-lg"
          >
            <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
              {ev.image ? (
                <SmartImage
                  src={ev.image}
                  alt={ev.name}
                  className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : null}
              {d && !Number.isNaN(d.getTime()) ? (
                <div className="absolute left-3 top-3 rounded-xl bg-white px-3 py-1.5 text-center shadow-md">
                  <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-royal">
                    {d.toLocaleDateString("en-GB", { month: "short" })}
                  </div>
                  <div className="font-heading text-lg leading-none text-navy">{d.getDate()}</div>
                </div>
              ) : null}
            </div>
            <div className="p-3.5">
              <h3 className="line-clamp-2 min-h-[2.4em] text-[15px] font-semibold leading-[1.2] tracking-tight text-navy">
                {ev.name}
              </h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {ev.date_label ? (
                  <span className="text-[12px] font-medium text-navy/50">
                    {ev.date_label}
                  </span>
                ) : null}
                {ev.price ? (
                  <span
                    style={{ textTransform: "none" }}
                    className="rounded-full bg-royal/10 px-2 py-0.5 text-[11px] font-semibold text-royal"
                  >
                    {ev.price}
                  </span>
                ) : null}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function MapPanel({
  districtSlug,
  pins,
  areaPins,
  center,
  tall,
  districtName,
}: {
  districtSlug: string;
  pins: IsoPin[];
  areaPins: AreaPin[];
  center?: { lat: number; lng: number } | null;
  tall?: boolean;
  districtName: string;
}) {
  const height = tall ? 560 : 420;
  const hasIllustratedMap = districtSlug === "train-factory" || districtSlug === "konepaja";
  const [mode, setMode] = useState<"2d" | "3d">(hasIllustratedMap ? "3d" : "2d");
  // Admin-placed numbered markers for this district, when there are any.
  const { data: manualPins } = useQuery({
    queryKey: ["district-map-pins", districtSlug],
    queryFn: async (): Promise<ManualPin[]> => {
      const { data, error } = await supabase
        .from("district_map_pins")
        .select("id, n, name, address, place_slug, category, x, y, area, area_style, image_url, tags")
        .eq("district_slug", districtSlug)
        .order("n");
      if (error) throw error;
      return (data ?? []).map((r) => ({
        id: r.id,
        n: r.n,
        label: r.name,
        address: r.address,
        slug: r.place_slug,
        category: r.category,
        x: r.x,
        y: r.y,
        area: (Array.isArray(r.area) ? r.area : null) as AreaPoly | null,
        areaStyle: ((r as { area_style?: unknown }).area_style ?? null) as AreaStyle | null,
        image: (r as { image_url?: string | null }).image_url ?? null,
        anchor: Array.isArray(r.tags) && r.tags.includes("anchor"),
      }));
    },
  });
  return (
    <>
      {mode === "3d" ? (
        <div className="relative">
          <IsometricDistrictMap
            pins={pins}
            manualPins={manualPins ?? []}
            districtName={districtName}
            height={height}
          />
          <button
            type="button"
            onClick={() => setMode("2d")}
            style={{ textTransform: "none" }}
            className="absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full border border-navy/10 bg-white/95 px-3 py-1.5 text-xs font-bold text-navy shadow-sm hover:bg-slate-50"
          >
            Real map
          </button>
        </div>
      ) : (
        <div className="relative">
          <ClientOnly fallback={<div style={{ height }} className="animate-pulse rounded-2xl bg-slate-100" />}>
            <Suspense fallback={<div style={{ height }} className="animate-pulse rounded-2xl bg-slate-100" />}>
              <AreaMap pins={areaPins} districtName={districtName} center={center} height={height} />
            </Suspense>
          </ClientOnly>
          {hasIllustratedMap && (
            <button
              type="button"
              onClick={() => setMode("3d")}
              style={{ textTransform: "none" }}
              className="absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full border border-navy/10 bg-white/95 px-3 py-1.5 text-xs font-bold text-navy shadow-sm hover:bg-slate-50"
            >
              Illustrated 3D
            </button>
          )}
        </div>
      )}
    </>
  );
}


type InfoKind = "parking" | "transport" | "address" | "access" | "hours" | "info";

const INFO_ICONS: Record<InfoKind, React.ReactNode> = {
  parking: <ParkingMark className="size-5" />,
  transport: <TramMark className="size-5" />,
  address: <MapMarkerPinIcon className="size-5" />,
  access: <Accessibility className="size-5" strokeWidth={2.1} />,
  hours: <Clock3 className="size-5" strokeWidth={2.1} />,
  info: <Info className="size-5" strokeWidth={2.1} />,
};

function infoKind(title: string): InfoKind {
  const t = title.toLowerCase();
  if (/park/.test(t)) return "parking";
  if (/accessib|wheelchair|step-free|step free/.test(t)) return "access";
  if (/address/.test(t)) return "address";
  if (/here|transport|metro|tram|bus|direction/.test(t)) return "transport";
  if (/hour|open/.test(t)) return "hours";
  return "info";
}

function displayInfoTitle(title: string): string {
  if (/accessib/i.test(title)) return "Accessibility";
  return title;
}

function InfoCards({
  district,
  onTab,
}: {
  district: { practical_info: { title: string; body: string }[]; address: string | null; website: string | null };
  onTab?: (tab: TabKey) => void;
}) {
  const cards = [
    ...district.practical_info,
    ...(district.address ? [{ title: "Address", body: district.address }] : []),
  ];
  if (cards.length === 0) return <EmptyState text="Practical information hasn’t been added yet." />;

  const mapHref = district.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(district.address)}`
    : null;
  const dirHref = district.address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(district.address)}`
    : mapHref;

  return (
    <div className="overflow-hidden rounded-[1.5rem] border border-navy/[0.08] bg-white">
      <div className="grid grid-cols-1 divide-y divide-navy/[0.08] sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 lg:divide-x lg:divide-navy/[0.08]">
        {cards.map((info, i) => {
          const kind = infoKind(info.title);
          let action: React.ReactNode = null;
          if (kind === "address" && dirHref) {
            action = (
              <a href={dirHref} target="_blank" rel="noreferrer" className={INFO_LINK}>
                Directions <ArrowRight className="size-3.5" />
              </a>
            );
          } else if (kind === "transport") {
            action = (
              <a href={dirHref ?? "https://www.hsl.fi/en"} target="_blank" rel="noreferrer" className={INFO_LINK}>
                {dirHref ? "Directions" : "HSL journey planner"} <ArrowRight className="size-3.5" />
              </a>
            );
          } else if (kind === "hours" && onTab) {
            action = (
              <button type="button" onClick={() => onTab("events")} className={INFO_LINK}>
                See event details <ArrowRight className="size-3.5" />
              </button>
            );
          } else if (kind === "parking") {
            const href = district.website
              ? district.website
              : district.address
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`parking ${district.address}`)}`
                : null;
            action = href ? (
              <a href={href} target="_blank" rel="noreferrer" className={INFO_LINK}>
                Parking map <ArrowRight className="size-3.5" />
              </a>
            ) : null;
          } else if (kind === "access" && onTab) {
            action = (
              <button type="button" onClick={() => onTab("restaurants")} className={INFO_LINK}>
                See venues <ArrowRight className="size-3.5" />
              </button>
            );
          }

          return (
            <article
              key={info.title}
              className={`flex min-h-[220px] flex-col px-5 py-5 sm:min-h-[240px] sm:px-6 sm:py-6 ${
                i % 2 === 1 ? "sm:border-l sm:border-navy/[0.08] lg:border-l-0" : ""
              } ${i >= 2 ? "sm:border-t sm:border-navy/[0.08] lg:border-t-0" : ""}`}
            >
              <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-navy/[0.06] text-navy">
                {INFO_ICONS[kind]}
              </span>
              <h3 className="mt-4 text-[15px] font-semibold leading-none tracking-tight text-navy">
                {displayInfoTitle(info.title)}
              </h3>
              <p className="mt-2.5 flex-1 text-[13px] font-medium leading-[1.55] text-navy/55">
                {info.body}
              </p>
              {action ? <div className="mt-5">{action}</div> : <div className="mt-5 h-[13px]" />}
            </article>
          );
        })}
      </div>
    </div>
  );
}

const INFO_LINK =
  "inline-flex items-center gap-1 text-[13px] font-semibold leading-none text-royal normal-case transition-colors hover:text-navy";


function EmptyState({ text }: { text: string }) {
  return (
    <div
      style={{ textTransform: "none" }}
      className="rounded-2xl border border-dashed border-navy/15 bg-white/60 px-6 py-12 text-center text-sm text-navy/50"
    >
      {text}
    </div>
  );
}

function TabLink({
  onClick,
  children,
  className,
}: {
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 text-[12px] font-semibold leading-none text-navy/60 normal-case hover:text-navy sm:text-[13px] ${className ?? ""}`}
    >
      {children}
    </button>
  );
}

function SectionHeading({ eyebrow, title, action }: { eyebrow: string; title: string; action?: React.ReactNode }) {
  return (
    <div className="mb-3.5 flex items-end justify-between gap-3 sm:mb-6">
      <div className="min-w-0">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/40 sm:mb-1.5 sm:tracking-[0.18em]">
          {eyebrow}
        </p>
        <h2 className="font-heading text-[20px] leading-tight tracking-tight text-navy sm:text-[28px]">{title}</h2>
      </div>
      {action}
    </div>
  );
}
